/**
 * Admin Metrics Dashboard Page (Slim Orchestrator)
 *
 * Manages state, data-fetching, and layout.
 * Chart and table sections are rendered by sub-components.
 */

import { useReducer, useEffect, useCallback, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../hooks/useAuth';
import {
  fetchMetricsReport,
  fetchOperationReport,
  fetchIndividualOperations,
  fetchUserBreakdown,
  fetchCountryBreakdown,
  fetchCategories,
  type MetricsFilters,
} from '../../services/metricsAPI';
import { extractErrorMessage } from '../../utils/httpUtils';
import { metricsReducer, initialMetricsState, type ViewMode } from './reducer';

// Sub-components
import { MetricsFilterBar, type TimePreset } from './components/MetricsFilterBar';
import { MetricsSummaryCards } from './components/MetricsSummaryCards';
import { OperationsTable } from './components/OperationsTable';
import { RecommendationsPanel } from './components/RecommendationsPanel';
import { IndividualOpsView } from './components/IndividualOpsView';
import { UsersView } from './components/UsersView';
import { CountriesView } from './components/CountriesView';

// Charts
import { CostVarianceChart } from './charts/CostVarianceChart';
import { ProcessingTimeChart } from './charts/ProcessingTimeChart';
import { RevenueMarginCard } from './charts/RevenueMarginCard';
import { UsageTrendsChart } from './charts/UsageTrendsChart';
import { RecommendationsAlert } from './charts/RecommendationsAlert';

// Fixed period constant (was useState(7) — never changed)
const PERIOD_DAYS = 7;

export function AdminMetricsPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, claims, getIdToken } = useAuth();

  const [state, dispatch] = useReducer(metricsReducer, initialMetricsState);
  // Per-sub-view inline error (does not take over the full page like the shared `error` state)
  const [subViewError, setSubViewError] = useState<string | null>(null);

  const {
    loading,
    error,
    environment,
    activeView,
    timePreset,
    customStart,
    customEnd,
    selectedCategory,
    userFilter,
    countryFilter,
    categories,
    authToken,
    report,
    selectedOperation,
    operationReport,
    loadingDetail,
    individualOps,
    opsLoading,
    opsCursor,
    userBreakdown,
    usersLoading,
    countryBreakdown,
    countriesLoading,
  } = state;

  const isAdmin = claims?.admin === true;

  // Build unified filters
  const buildFilters = useCallback((): MetricsFilters => {
    const filters: MetricsFilters = {};

    if (timePreset === '1h') filters.hours = 1;
    else if (timePreset === '8h') filters.hours = 8;
    else if (timePreset === '24h') filters.hours = 24;
    else if (timePreset === '7d') filters.days = 7;
    else if (timePreset === '30d') filters.days = 30;
    else if (timePreset === 'custom') {
      if (customStart) filters.start_date = new Date(customStart).toISOString();
      if (customEnd) filters.end_date = new Date(customEnd).toISOString();
    } else {
      filters.days = PERIOD_DAYS;
    }

    if (selectedCategory && categories) {
      const ops = categories[selectedCategory];
      if (ops) filters.operations = ops;
    }
    if (environment) filters.environment = environment;
    if (userFilter) filters.user_id = userFilter;
    if (countryFilter) filters.country_code = countryFilter;

    return filters;
  }, [
    timePreset,
    customStart,
    customEnd,
    selectedCategory,
    environment,
    userFilter,
    countryFilter,
    categories,
  ]);

  // Load categories on mount
  useEffect(() => {
    const load = async () => {
      if (!user || !isAdmin) return;
      try {
        const token = await getIdToken();
        if (!token) return;
        dispatch({ type: 'SET_AUTH_TOKEN', token });
        const cats = await fetchCategories(token);
        dispatch({ type: 'SET_CATEGORIES', categories: cats });
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    void load();
  }, [user, isAdmin, getIdToken]);

  // Load metrics report (summary view)
  const loadMetrics = useCallback(async () => {
    if (!user) {
      dispatch({ type: 'SET_ERROR', error: 'Authentication required' });
      dispatch({ type: 'SET_LOADING', loading: false });
      return;
    }
    if (!isAdmin) {
      dispatch({ type: 'SET_ERROR', error: 'Admin access required' });
      dispatch({ type: 'SET_LOADING', loading: false });
      return;
    }
    try {
      dispatch({ type: 'SET_LOADING', loading: true });
      dispatch({ type: 'SET_ERROR', error: null });
      const token = await getIdToken();
      if (!token) {
        dispatch({ type: 'SET_ERROR', error: 'Failed to get authentication token' });
        return;
      }
      dispatch({ type: 'SET_AUTH_TOKEN', token });
      const data = await fetchMetricsReport(PERIOD_DAYS, token, environment);
      dispatch({ type: 'SET_REPORT', report: data });
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        error: extractErrorMessage(err, 'Failed to load metrics'),
      });
    } finally {
      dispatch({ type: 'SET_LOADING', loading: false });
    }
  }, [user, isAdmin, environment, getIdToken]);

  // Load individual operations
  const loadIndividualOps = useCallback(
    async (append = false) => {
      if (!user || !isAdmin) return;
      try {
        dispatch({ type: 'SET_OPS_LOADING', loading: true });
        const token = await getIdToken();
        if (!token) return;
        const filters = buildFilters();
        const data = await fetchIndividualOperations(
          filters,
          token,
          append ? (opsCursor ?? undefined) : undefined,
          50
        );
        dispatch({ type: 'SET_INDIVIDUAL_OPS', ops: data.items, append });
        dispatch({ type: 'SET_OPS_CURSOR', cursor: data.next_cursor });
      } catch (err) {
        console.error('Failed to load individual operations:', err);
        setSubViewError(extractErrorMessage(err, 'Failed to load operations'));
      } finally {
        dispatch({ type: 'SET_OPS_LOADING', loading: false });
      }
    },
    [user, isAdmin, getIdToken, buildFilters, opsCursor]
  );

  // Load user breakdown
  const loadUserBreakdown = useCallback(async () => {
    if (!user || !isAdmin) return;
    try {
      dispatch({ type: 'SET_USERS_LOADING', loading: true });
      const token = await getIdToken();
      if (!token) return;
      const data = await fetchUserBreakdown(buildFilters(), token);
      dispatch({ type: 'SET_USER_BREAKDOWN', breakdown: data });
    } catch (err) {
      console.error('Failed to load user breakdown:', err);
      setSubViewError(extractErrorMessage(err, 'Failed to load user breakdown'));
    } finally {
      dispatch({ type: 'SET_USERS_LOADING', loading: false });
    }
  }, [user, isAdmin, getIdToken, buildFilters]);

  // Load country breakdown
  const loadCountryBreakdown = useCallback(async () => {
    if (!user || !isAdmin) return;
    try {
      dispatch({ type: 'SET_COUNTRIES_LOADING', loading: true });
      const token = await getIdToken();
      if (!token) return;
      const data = await fetchCountryBreakdown(buildFilters(), token);
      dispatch({ type: 'SET_COUNTRY_BREAKDOWN', breakdown: data });
    } catch (err) {
      console.error('Failed to load country breakdown:', err);
      setSubViewError(extractErrorMessage(err, 'Failed to load country breakdown'));
    } finally {
      dispatch({ type: 'SET_COUNTRIES_LOADING', loading: false });
    }
  }, [user, isAdmin, getIdToken, buildFilters]);

  useEffect(() => {
    // Clear sub-view errors when switching views
    setSubViewError(null);
    if (activeView === 'summary') loadMetrics();
    else if (activeView === 'individual') loadIndividualOps(false);
    else if (activeView === 'users') loadUserBreakdown();
    else if (activeView === 'countries') loadCountryBreakdown();
  }, [
    activeView,
    buildFilters,
    loadMetrics,
    loadIndividualOps,
    loadUserBreakdown,
    loadCountryBreakdown,
  ]);

  // Handle row click for drill-down
  const handleRowClick = async (operation: string) => {
    if (selectedOperation === operation) {
      dispatch({ type: 'SET_SELECTED_OPERATION', operation: null });
      dispatch({ type: 'SET_OPERATION_REPORT', report: null });
      return;
    }
    dispatch({ type: 'SET_SELECTED_OPERATION', operation });
    dispatch({ type: 'SET_LOADING_DETAIL', loading: true });
    try {
      const token = await getIdToken();
      if (!token) return;
      const detail = await fetchOperationReport(operation, PERIOD_DAYS, token, environment);
      dispatch({ type: 'SET_OPERATION_REPORT', report: detail });
    } catch (err) {
      console.error('Failed to load operation detail:', err);
      setSubViewError(extractErrorMessage(err, 'Failed to load operation detail'));
    } finally {
      dispatch({ type: 'SET_LOADING_DETAIL', loading: false });
    }
  };

  // Guard: loading
  if (loading && activeView === 'summary') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-white/70">{t('common.loading')}</div>
      </div>
    );
  }

  // Guard: error / not admin
  if (error || !isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-black/30 backdrop-blur-xl rounded-xl border border-red-500/30 p-6 max-w-md">
          <div className="text-red-400 text-center">{error ?? 'Admin access required'}</div>
        </div>
      </div>
    );
  }

  const filters = buildFilters();

  return (
    <div className="w-full min-h-screen px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="section-heading-xl">{t('admin.metrics.title')}</h1>
        <p className="text-white/70 text-sm sm:text-base">{t('admin.metrics.subtitle')}</p>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6 flex flex-wrap gap-2">
        {(['summary', 'individual', 'users', 'countries'] as ViewMode[]).map((view) => (
          <button
            key={view}
            onClick={() => dispatch({ type: 'SET_ACTIVE_VIEW', view })}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
              activeView === view
                ? 'bg-purple-600/30 text-purple-300 border border-purple-500/50'
                : 'bg-black/20 text-white/60 border border-white/10 hover:border-white/20'
            }`}
          >
            {t(`admin.metrics.${view}View`)}
          </button>
        ))}
      </div>

      {/* Filter Bar */}
      <MetricsFilterBar
        timePreset={timePreset}
        onTimePresetChange={(preset: TimePreset) => dispatch({ type: 'SET_TIME_PRESET', preset })}
        customStart={customStart}
        onCustomStartChange={(start: string) => dispatch({ type: 'SET_CUSTOM_START', start })}
        customEnd={customEnd}
        onCustomEndChange={(end: string) => dispatch({ type: 'SET_CUSTOM_END', end })}
        selectedCategory={selectedCategory}
        onCategoryChange={(category: string | null) =>
          dispatch({ type: 'SET_SELECTED_CATEGORY', category })
        }
        categories={categories}
        environment={environment}
        onEnvironmentChange={(env: string | undefined) =>
          dispatch({ type: 'SET_ENVIRONMENT', environment: env })
        }
        userFilter={userFilter}
        onUserFilterClear={() => dispatch({ type: 'CLEAR_USER_FILTER' })}
        countryFilter={countryFilter}
        onCountryFilterClear={() => dispatch({ type: 'CLEAR_COUNTRY_FILTER' })}
      />

      {/* Summary View */}
      {activeView === 'summary' && report && (
        <>
          <MetricsSummaryCards report={report} />

          {/* Recommendations Alert Banner */}
          {report.recommendations.length > 0 && (
            <div className="mb-6">
              <RecommendationsAlert recommendations={report.recommendations} maxAlerts={3} />
            </div>
          )}

          {/* Charts Section */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6 sm:mb-8 items-start">
            {/* Cost Variance Chart */}
            <div className="panel-glass-card-sm">
              <h3 className="text-base font-semibold text-white mb-4">
                {t('admin.metrics.chartCostVariance')}
              </h3>
              <CostVarianceChart stats={report.operation_stats} />
            </div>

            {/* Processing Time Distribution */}
            <div className="panel-glass-card-sm">
              <h3 className="text-base font-semibold text-white mb-4">
                {t('admin.metrics.chartProcessingTime')}
              </h3>
              <ProcessingTimeChart stats={report.operation_stats} />
            </div>

            {/* Revenue Margin Analysis */}
            <div className="panel-glass-card-sm">
              <h3 className="text-base font-semibold text-white mb-4">
                {t('admin.metrics.chartRevenueMargin')}
              </h3>
              <RevenueMarginCard stats={report.operation_stats} />
            </div>

            {/* Usage Trends Chart */}
            <div className="panel-glass-card-sm">
              <h3 className="text-base font-semibold text-white mb-4">
                {t('admin.metrics.chartUsageTrends')}
              </h3>
              {authToken && <UsageTrendsChart filters={filters} token={authToken} bucket="day" />}
            </div>
          </div>

          {/* Operations Table */}
          <OperationsTable
            report={report}
            selectedOperation={selectedOperation}
            operationReport={operationReport}
            loadingDetail={loadingDetail}
            onRowClick={handleRowClick}
          />

          {/* Pricing Recommendations */}
          <RecommendationsPanel recommendations={report.recommendations} />
        </>
      )}

      {/* Inline error banner for sub-view and detail-load errors (does not block the whole page) */}
      {subViewError && (
        <div className="mb-4 p-4 bg-black/30 backdrop-blur-xl rounded-xl border border-red-500/30 flex items-center justify-between gap-4">
          <span className="text-red-400 text-sm">{subViewError}</span>
          <button
            onClick={() => setSubViewError(null)}
            className="text-white/40 hover:text-white/80 text-xs underline shrink-0"
          >
            {t('common.dismiss', 'Dismiss')}
          </button>
        </div>
      )}

      {/* Individual Operations View */}
      {activeView === 'individual' && (
        <IndividualOpsView
          ops={individualOps}
          loading={opsLoading}
          cursor={opsCursor}
          onLoadMore={() => loadIndividualOps(true)}
        />
      )}

      {/* Users View */}
      {activeView === 'users' && (
        <UsersView
          users={userBreakdown}
          loading={usersLoading}
          onFilterByUser={(uid) => {
            dispatch({ type: 'SET_USER_FILTER', filter: uid });
            dispatch({ type: 'SET_ACTIVE_VIEW', view: 'summary' });
          }}
        />
      )}

      {/* Countries View */}
      {activeView === 'countries' && (
        <CountriesView
          countries={countryBreakdown}
          loading={countriesLoading}
          onFilterByCountry={(cc) => {
            dispatch({ type: 'SET_COUNTRY_FILTER', filter: cc });
            dispatch({ type: 'SET_ACTIVE_VIEW', view: 'summary' });
          }}
        />
      )}
    </div>
  );
}
